import Keycloak from 'keycloak-js';
import keycloakConfig from './keycloakConfig';
import { resolveRoleFromToken } from 'helpers/roles';

class KeycloakService {
	private readonly keycloak: Keycloak;
	private readonly minTokenValidityInSeconds = 880;

	private storeTokens(): void {
		localStorage.setItem('kc_token', this.keycloak.token ?? '');
		localStorage.setItem('kc_refreshToken', this.keycloak.refreshToken ?? '');
	}

	constructor() {
		this.keycloak = new Keycloak({
			url: keycloakConfig.url,
			realm: keycloakConfig.realm,
			clientId: keycloakConfig.clientId
		});
	}

	public init = (onAuthenticatedCallback: () => void, onError?: () => void): void => {
		const storedToken = localStorage.getItem('kc_token');
		const storedRefreshToken = localStorage.getItem('kc_refreshToken');

		this.keycloak
			.init({
				onLoad: 'login-required',
				checkLoginIframe: false,
				token: storedToken || undefined,
				refreshToken: storedRefreshToken || undefined
			})
			.then((authenticated) => {
				if (authenticated) {
					this.storeTokens();
					onAuthenticatedCallback();
				} else {
					console.warn('[Keycloak] User not authenticated. Redirecting to login...');
					this.keycloak.login();
				}
			})
			.catch((error) => this.handleInitError(error, onError));

		// refresca automáticamente el token
		setInterval(
			() => {
				this.updateToken()
					.then((refreshed) => {
						if (refreshed) this.storeTokens();
					})
					.catch(() => {
						console.warn('[Keycloak] Token refresh failed, logging out...');
						this.logout();
					});
			},
			(this.minTokenValidityInSeconds + 5) * 1000
		);
	};

	private readonly handleInitError = (error: unknown, onError?: () => void): void => {
		console.error('[Keycloak] Init FAILED with an exception:', error);
		if (onError) onError();
	};

	public login = (): void => {
		this.keycloak.login();
	};

	public logout = (): void => {
		localStorage.removeItem('kc_token');
		localStorage.removeItem('kc_refreshToken');
		this.keycloak.logout({ redirectUri: window.location.origin });
	};

	public getToken = (): string | undefined => {
		return this.keycloak.token;
	};

	public isLoggedIn = (): boolean => {
		return !!this.keycloak.token;
	};

	public updateToken = (minValidity = 880): Promise<boolean> => {
		return this.keycloak.updateToken(minValidity);
	};

	public getUserName = (): string | undefined => {
		return this.keycloak.tokenParsed?.preferred_username;
	};

	public getUserEmail = (): string | undefined => {
		return this.keycloak.tokenParsed?.email;
	};

	public getFirstName = (): string | undefined => {
		return this.keycloak.tokenParsed?.given_name;
	};

	public getLastName = (): string | undefined => {
		return this.keycloak.tokenParsed?.family_name;
	};

	public getUserRoles = (): string[] => {
		// El rol sale solo de los subgrupos de /Bienes Fiscales (claims `groups` y `grupos`); los roles de cliente no cuentan
		const role = resolveRoleFromToken(this.keycloak.tokenParsed);
		return role ? [role] : [];
	};

	public getUserPermissions = (): string[] => {
		return this.keycloak.tokenParsed?.Permisos || [];
	};

	public getUserUuid = (): string | undefined => {
		return this.keycloak.idTokenParsed?.sub;
	};

	public getKeycloakInstance = (): Keycloak => {
		return this.keycloak;
	};
}

export default new KeycloakService();
